import { useEffect } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import { AccountType } from "features/user/types";

const SESSION_KEY = "guest_login_prompt_shown";

/**
 * 体験ユーザー（guest）が登録から7日以上経過した場合、
 * アプリ起動時にログイン促進ダイアログを表示するフック
 *
 * 表示条件:
 * - parent.account_type === AccountType.GUEST (体験ユーザー)
 * - dayjs().diff(dayjs(parent.created_at), "day") >= 7 (登録から7日以上経過)
 * - セッション中に未表示（sessionStorageで管理）
 */
export const useGuestLoginPrompt = () => {
  const handleDialog = useDialog();
  const parent = useSelector((state: RootState) => state.user.parent);

  useEffect(() => {
    if (!parent) return;
    if (parent.account_type !== AccountType.GUEST) return;

    const daysSinceCreation = dayjs().diff(dayjs(parent.created_at), "day");
    if (daysSinceCreation < 7) return;

    // セッション中に既に表示済みなら再表示しない
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // 他のダイアログとの競合を回避するため、少し遅延させて表示
    const timer = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, "true");
      handleDialog({
        type: DialogType.RESTRICTED_FEATURE,
        value: true,
        args: { source: RestrictedFeatureSource.GUEST_LOGIN_PROMPT },
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [parent, handleDialog]);
};
